import { useEffect, useMemo, useState } from "react";
import { CircularProgress, Skeleton, Switch } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Close } from "@mui/icons-material";
import Modal from "components/ui/Modal";
import { capitalEachLetter } from "utils/capitalEachLetter";
import { useUpdateUserAccess, useUserAccess } from "./Api/myBinnysAccessApi";

// Feature keys must match backend myBinnyCompanyEmployeeVerfiyRights(...) names
const MAIN_TABS = [
  { key: "Purchases", label: "Purchases", info: "Browse designs and add to cart" },
  { key: "Catalogs", label: "Catalogs", info: "View curated design catalogs" },
  { key: "Dues", label: "Dues", info: "Invoices and amounts to pay" },
  { key: "WIP", label: "Work in Progress", info: "Track your orders under production" },
];

const MORE_FEATURES = [
  { key: "Ledger", label: "Ledger", info: "Full account statement" },
  { key: "Order", label: "Order", info: "Place orders on your behalf" },
  { key: "NPD Orders", label: "NPD Orders", info: "Request new custom designs" },
  { key: "Default Customisation", label: "Default Customisation", info: "Set default gold & diamond preferences" },
  { key: "Service Request", label: "Service Request", info: "Raise repair or quality-check requests" },
];

const TOTAL_FEATURES = MAIN_TABS.length + MORE_FEATURES.length;

// Vision portal palette
const COLORS = {
  bg: "#000000",
  card: "#191818",
  border: "#444444",
  text: "#FFFFFF",
  subText: "#8B8B8B",
  green: "#50C878",
  greenSoft: "rgba(80, 200, 120, 0.12)",
  red: "#E77B7B",
};

const AVATAR_COLORS = ["#6E5BAA", "#8A7A3C", "#7A6A50", "#5E6B45", "#A0613F", "#7A6688", "#4F6D8A"];

const avatarColor = (name = "") => {
  const sum = [...name].reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
};

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

// Square-thumb switch, same look as IOSSwitch
const AccessSwitch = styled(Switch)(() => ({
  width: 52,
  height: 28,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 3,
    "&.Mui-checked": {
      transform: "translateX(24px)",
      color: "#fff",
      "& + .MuiSwitch-track": { backgroundColor: COLORS.green, borderColor: COLORS.green, opacity: 1 },
    },
    "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.4 },
    "&.Mui-disabled .MuiSwitch-thumb": { opacity: 0.7 },
  },
  "& .MuiSwitch-thumb": { width: 22, height: 22, borderRadius: 4, backgroundColor: "#FFFFFF", boxShadow: "none" },
  "& .MuiSwitch-track": { borderRadius: 4, backgroundColor: "#000000", border: `1px solid ${COLORS.border}`, boxSizing: "border-box", opacity: 1 },
}));

const styles = {
  // Shared tray Modal -> bottom-right floating, 70% of viewport height
  tray: { width: "420px", height: "70vh", padding: "0px", scroll: "hidden", overflowX: "hidden" },
  paper: {
    height: "100%",
    backgroundColor: COLORS.bg,
    color: COLORS.text,
    fontFamily: "Poppins",
    border: `1px solid ${COLORS.border}`,
    borderRadius: "5px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", backgroundColor: COLORS.card, borderBottom: `1px solid ${COLORS.border}` },
  eyebrow: { fontSize: 11, fontWeight: 500, letterSpacing: 1, color: COLORS.subText },
  title: { fontSize: 20, fontWeight: 500, lineHeight: "26px" },
  closeBtn: {
    width: 34,
    height: 34,
    borderRadius: 6,
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.bg,
    color: COLORS.text,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  // Profile card stays fixed under header, only feature list scrolls
  profileWrap: { padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}` },
  body: { flex: 1, overflowY: "auto", padding: "18px 20px 20px", scrollbarWidth: "none" },
  card: { backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 6 },
  profile: { display: "flex", alignItems: "center", gap: 14, padding: 14 },
  avatar: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 16,
    flexShrink: 0,
  },
  name: { fontSize: 16, fontWeight: 500 },
  meta: { fontSize: 12, color: COLORS.subText, marginTop: 2 },
  sectionTitle: { fontSize: 12, fontWeight: 500, letterSpacing: 1, color: COLORS.subText },
  sectionInfo: { fontSize: 12, color: COLORS.subText, margin: "4px 0 10px" },
  row: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", gap: 12 },
  rowLabel: { fontSize: 14, fontWeight: 500 },
  rowInfo: { fontSize: 12, color: COLORS.subText, marginTop: 2 },
  notice: { fontSize: 12, borderRadius: 6, padding: "10px 14px", marginBottom: 18 },
  footer: { padding: "14px 20px", borderTop: `1px solid ${COLORS.border}`, backgroundColor: COLORS.card },
};

const Badge = ({ full, count }) => (
  <span
    style={{
      fontSize: 11,
      fontWeight: 600,
      padding: "4px 10px",
      borderRadius: 6,
      whiteSpace: "nowrap",
      color: full ? COLORS.green : COLORS.subText,
      backgroundColor: COLORS.bg,
      border: `1px solid ${full ? COLORS.green : COLORS.border}`,
    }}
  >
    {full ? "Full access" : `${count} of ${TOTAL_FEATURES}`}
  </span>
);

const FeatureSection = ({ title, info, items, selected, onToggle, disabled, lockLastOn }) => (
  <div style={{ marginBottom: 22 }}>
    <div style={styles.sectionTitle}>{title}</div>
    <div style={styles.sectionInfo}>{info}</div>
    <div style={styles.card}>
      {items.map((item, idx) => {
        const checked = selected.includes(item.key);
        const isLastOn = lockLastOn && checked && items.filter((i) => selected.includes(i.key)).length === 1;
        return (
          <div key={item.key} style={{ ...styles.row, borderTop: idx ? `1px solid ${COLORS.border}` : "none" }}>
            <div>
              <div style={styles.rowLabel}>{item.label}</div>
              <div style={styles.rowInfo}>{item.info}</div>
            </div>
            <AccessSwitch checked={checked} disabled={disabled || isLastOn} onChange={() => onToggle(item.key)} disableRipple />
          </div>
        );
      })}
    </div>
  </div>
);

const UserAccessTray = ({ open, onClose, user: selectedUser, canEdit }) => {
  // Keep last user while slide-out animation runs, else tray empties on close
  const [user, setUser] = useState(selectedUser);
  useEffect(() => {
    if (selectedUser) setUser(selectedUser);
  }, [selectedUser]);

  const employeeCompanyId = user?._id;
  const { data, isLoading, isError } = useUserAccess(employeeCompanyId);
  const { mutate: updateAccess, isPending } = useUpdateUserAccess();

  const savedPrimary = useMemo(() => data?.employeeRights?.primaryfeatures || [], [data]);
  const savedSecondary = useMemo(() => data?.employeeRights?.secondaryfeatures || [], [data]);

  const [primary, setPrimary] = useState([]);
  const [secondary, setSecondary] = useState([]);

  useEffect(() => {
    setPrimary(savedPrimary);
    setSecondary(savedSecondary);
  }, [savedPrimary, savedSecondary, open]);

  const toggle = (setter) => (key) => setter((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const sameSet = (a, b) => a.length === b.length && a.every((k) => b.includes(k));
  const isDirty = !sameSet(primary, savedPrimary) || !sameSet(secondary, savedSecondary);

  const isAdmin = !!data?.isAdmin;
  const enabledCount = [...MAIN_TABS, ...MORE_FEATURES].filter((f) => [...primary, ...secondary].includes(f.key)).length;
  const isFull = isAdmin || enabledCount === TOTAL_FEATURES;

  const name = capitalEachLetter(data?.employeeDetails?.employeeName || user?.clientUserName || "");
  const mobile = data?.employeeDetails?.employeeNumber || user?.clientUserMobileNumber;
  const editable = canEdit && !isAdmin && !isLoading && !isError;

  const handleSave = () => {
    updateAccess({ employeeCompanyId: user?._id, rights: { primaryfeatures: primary, secondaryfeatures: secondary } });
  };

  return (
    <Modal open={open} handleClose={onClose} style={styles.tray}>
      <div style={styles.paper}>
      <div style={styles.header}>
        <div>
          <div style={styles.eyebrow}>USER ACCESS</div>
          <div style={styles.title}>Team member</div>
        </div>
        <span style={styles.closeBtn} onClick={onClose}>
          <Close fontSize="small" />
        </span>
      </div>

      <div style={styles.profileWrap}>
        <div style={{ ...styles.card, ...styles.profile }}>
          <div style={{ ...styles.avatar, backgroundColor: avatarColor(name) }}>{initials(name)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={styles.name}>{name || "-"}</div>
            <div style={styles.meta}>
              {[data?.employeeCompanyName, mobile].filter(Boolean).join(" · ")}
            </div>
          </div>
          {!isLoading && <Badge full={isFull} count={enabledCount} />}
        </div>
      </div>

      <div style={styles.body}>
        {isAdmin && (
          <div style={{ ...styles.notice, color: COLORS.green, backgroundColor: COLORS.greenSoft }}>
            Admin users get full access to all features. Remove admin to customise access.
          </div>
        )}
        {isError && (
          <div style={{ ...styles.notice, color: COLORS.red, backgroundColor: "rgba(231, 123, 123, 0.12)" }}>
            Unable to load access for this user.
          </div>
        )}

        {isLoading ? (
          Array.from(new Array(6)).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={64} sx={{ bgcolor: COLORS.card, mb: 1.5, borderRadius: "6px" }} animation="wave" />
          ))
        ) : (
          <>
            <FeatureSection
              title="MAIN TABS"
              info="The app tabs this member can open. At least one must stay on."
              items={MAIN_TABS}
              selected={isAdmin ? MAIN_TABS.map((f) => f.key) : primary}
              onToggle={toggle(setPrimary)}
              disabled={!editable}
              lockLastOn
            />
            <FeatureSection
              title="MORE FEATURES"
              info="Extra tools this member can use inside the app."
              items={MORE_FEATURES}
              selected={isAdmin ? MORE_FEATURES.map((f) => f.key) : secondary}
              onToggle={toggle(setSecondary)}
              disabled={!editable}
            />
          </>
        )}
      </div>

      {canEdit && (
        <div style={styles.footer}>
          <button
            onClick={handleSave}
            disabled={!editable || !isDirty || !primary.length || isPending}
            style={{
              width: "100%",
              height: 42,
              borderRadius: 6,
              border: `1px solid ${editable && isDirty ? COLORS.green : COLORS.border}`,
              fontFamily: "Poppins",
              fontSize: 14,
              fontWeight: 500,
              cursor: editable && isDirty ? "pointer" : "not-allowed",
              color: editable && isDirty ? "#000" : COLORS.subText,
              backgroundColor: editable && isDirty ? COLORS.green : COLORS.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isPending ? <CircularProgress size={20} color="inherit" /> : "Save changes"}
          </button>
        </div>
      )}
      </div>
    </Modal>
  );
};

export default UserAccessTray;
